import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGlobe, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { getLocalizedPath, getUrlLanguageCode, removeLanguagePrefix } from '../utils/i18n-routes';

const languages = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'zh-CN', name: '简体中文', flag: '🇨🇳' },
  { code: 'zh-TW', name: '繁體中文', flag: '🇹🇼' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'id', name: 'Bahasa Indonesia', flag: '🇮🇩' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'ja', name: '日本語', flag: '🇯🇵' },
  { code: 'ko', name: '한국어', flag: '🇰🇷' },
  { code: 'pt-BR', name: 'Português (Brasil)', flag: '🇧🇷' },
  { code: 'es-ES', name: 'Español (España)', flag: '🇪🇸' },
  { code: 'es-MX', name: 'Español (México)', flag: '🇲🇽' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
];

const LanguageSwitcher: React.FC = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const changeLanguage = (languageCode: string) => {
    i18n.changeLanguage(languageCode);
    setIsOpen(false);
    
    // Save to localStorage (i18next does this automatically)
    localStorage.setItem('i18nextLng', languageCode);
    
    // Navigate to the same page but with new language prefix for SEO
    const currentPath = removeLanguagePrefix(location.pathname);
    const newPath = getLocalizedPath(currentPath, languageCode);
    
    // Check if we're on an exam page - if so, refresh to pull language-related text
    const isExamPage = location.pathname.includes('/exam/');
    
    if (isExamPage) {
      // Reload the page to fetch exam data with new language
      window.location.href = newPath;
    } else {
      // Only navigate if the path would change
      if (newPath !== location.pathname) {
        navigate(newPath);
      }
    }
    
    // Optionally update user preference in backend
    // This would require an API call to update the user's preferred_language
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="gp-nav-link"
        aria-label="Change language"
      >
        <FontAwesomeIcon icon={faGlobe} className="text-sm" />
        <span className="text-sm font-medium hidden sm:inline">{currentLanguage.flag}</span>
        <FontAwesomeIcon icon={faChevronDown} className={`text-xs transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="gp-dropdown w-56 max-h-96">
          {languages.map((language) => (
            <button
              key={language.code}
              onClick={() => changeLanguage(language.code)}
              className={`gp-dropdown-item flex items-center gap-3 ${
                i18n.language === language.code ? 'gp-dropdown-item-active' : ''
              }`}
            >
              <span className="text-base">{language.flag}</span>
              <span className="flex-1">{language.name}</span>
              {i18n.language === language.code && (
                <span style={{ color: 'var(--gp-brand)' }}>✓</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
